"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type RevealOnScrollProps = {
  children: React.ReactNode;
  className?: string;
  once?: boolean;
  threshold?: number;
  y?: number;
  duration?: number;
  enableAnimation?: boolean;
  scaleStart?: number;
  x?: number;
  delay?: number; // in milliseconds
  /**
   * "span" lets a reveal sit INSIDE a heading (each line of a two-line section title is
   * revealed on its own delay, but the title is one <h2>). A heading may only hold
   * phrasing content, so the default <div> wrappers are invalid there. Both wrappers then
   * render as spans with `display: block`, since a transform does nothing on an inline box.
   * Don't pass a display utility in `className` with it: the wrapper already sets one.
   */
  as?: "div" | "span";
};

/**
 * `useLayoutEffect` on the client, `useEffect` on the server — React warns when
 * `useLayoutEffect` runs during SSR. We need the layout variant so the below-the-fold
 * hide happens BEFORE the browser paints, otherwise content would flash in and out.
 */
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * RevealOnScroll is a component that reveals the children when they are visible in the viewport.
 * @param children - The children to reveal.
 * @param className - The class name to apply to the container.
 * @param once - If true, the animation will only trigger once.
 * @param threshold - The threshold to trigger the animation.
 * @param y - The distance to move the children down from the top.
 * @param duration - The duration of the animation.
 * @param scaleStart - The scale to start the animation at.
 * @param x - The distance to move the children left or right from the center.
 * @param enableAnimation - If false, the animation will be disabled and children will be visible immediately.
 * @param delay - The delay before the animation starts, in milliseconds.
 * @returns A React component that reveals its children on scroll.
 */
function RevealOnScroll({
  children,
  className = "",
  once = false, // if true, the animation will only trigger once
  threshold = 0.2, // if 20% visible trigger the animation
  y = 100, // 100px down from the top
  x=0, // for left and right animation
  duration = 800, // 800ms duration of the animation
  enableAnimation = true,
  scaleStart = 1,
  delay = 0, // delay before the animation starts
  as = "div",
}: RevealOnScrollProps) {
  const ref = useRef<HTMLElement | null>(null);
  const Tag = as as React.ElementType;
  const display = as === "span" ? "block" : undefined;
  /**
   * Starts TRUE so the server renders `opacity: 1`.
   *
   * This used to start `false`, so every wrapped block shipped as `opacity: 0` and only
   * became visible after React hydrated and an IntersectionObserver fired. On
   * /online-mba that made an <h2> the LCP element with **12,758 ms of render delay** —
   * 93% of a 13.7 s LCP — because the text existed in the HTML but could not paint.
   * It also meant that if JS failed to load, every reveal block stayed invisible forever.
   *
   * Now the server paints content immediately and the layout effect below hides only the
   * blocks that are actually off-screen, before the browser paints, so the scroll
   * animation still works for everything below the fold.
   */
  const [isVisible, setIsVisible] = useState(true);
  const isVisibleRef = useRef(true);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didInitRef = useRef(false);

  /**
   * Runs once, before first paint: anything already on screen stays visible (it is
   * above the fold — animating it in would only delay LCP), anything off screen is
   * hidden so the observer below can reveal it on scroll as before.
   */
  useIsomorphicLayoutEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    if (!enableAnimation) return;
    const node = ref.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const isOnScreen = rect.top < viewportH && rect.bottom > 0;

    if (!isOnScreen) {
      isVisibleRef.current = false;
      setIsVisible(false);
    }
  }, [enableAnimation]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    // No observer needed when animation is disabled. `enableAnimation` can toggle
    // at runtime (e.g. suppressing reveals after a user interaction), so this must
    // stay a hook that runs every render — never gate it behind an early return.
    if (!enableAnimation) return;
    const node = ref.current;
    if (!node) return;
    const enterThreshold = Math.min(Math.max(threshold, 0), 1);
    const exitThreshold = Math.min(0.05, enterThreshold * 0.5);
    const observerThresholds = Array.from(
      new Set([0, exitThreshold, enterThreshold]),
    ).sort((a, b) => a - b);

    const observer = new IntersectionObserver(
      ([entry]) => {
        const ratio = entry.intersectionRatio;

        if (ratio >= enterThreshold) {
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
          }
          if (!isVisibleRef.current) {
            isVisibleRef.current = true;
            setIsVisible(true);
            if (once) observer.unobserve(node);
          }
          return;
        }

        if (!once && ratio <= exitThreshold && isVisibleRef.current) {
          if (hideTimeoutRef.current) return;
          hideTimeoutRef.current = setTimeout(() => {
            if (!once) {
              isVisibleRef.current = false;
              setIsVisible(false);
            }
            hideTimeoutRef.current = null;
          }, 140);
          return;
        }

        if (hideTimeoutRef.current && ratio > exitThreshold) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      },
      {
        threshold: observerThresholds,
        // Shrink viewport bottom slightly so reveal starts closer to on-screen entry.
        // This prevents the animation from finishing before users see it during slow scroll.
        rootMargin: "0px 0px -4% 0px",
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, [once, threshold, enableAnimation]);

  // Rendered after all hooks so the hook order is identical whether or not the
  // animation is enabled — toggling `enableAnimation` at runtime is safe.
  if (!enableAnimation) {
    /**
     * The wrapper still renders, carrying `className`, because callers put LAYOUT on this
     * prop — never animation styling. Across the 34 call sites that pass one, every single
     * class is positional: `order-*`, `w-full`, `h-full`, `min-w-0`, `shrink-0`, `flex-1`,
     * `sm:hidden`.
     *
     * This used to `return children`, which silently dropped all of it whenever animation
     * was switched off. The visible failure was Banner 2 on mobile: both of its columns use
     * `contents`, so the reveal wrappers ARE the flex items that carry `order-2` (media) and
     * `order-4` (CTAs). With the wrapper gone both fell back to `order: 0`, which sorts
     * ahead of every positive value — so the CTAs jumped above the artwork and the title.
     * `shrink-0` in ProgramFee2Variant3 and `min-w-0` in KeyNumbers2 were being lost the
     * same way.
     *
     * Only wrapped when there is something to carry, so the ~112 call sites that pass no
     * className keep the leaner DOM they have today.
     */
    if (!className) return children;
    return <Tag className={cn("p-0 m-0", display, className)}>{children}</Tag>;
  }

  return (
    <Tag ref={ref} className={cn("p-0 m-0", display, className)}>
      <Tag
        className={cn("transition-all ease-out will-change-transform p-0 m-0 w-full h-full", display)}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible
            ? "translate(0px, 0px) scale(1)"
            : `translate(${x}px, ${y}px) scale(${scaleStart})`,
          transitionDuration: `${duration}ms`,
          transitionDelay: `${delay}ms`,
        }}
      >
        {children}
      </Tag>
    </Tag>
  );
}

RevealOnScroll.displayName = "RevealOnScroll";
export default RevealOnScroll;
